/**
 * Gameplay-chapter director — the complete Latin Domino teaching sequence
 * (sections A–H) as a declarative step table plus a pure reducer.
 *
 * Steps advance on exactly one awaited signal each: a Continue press, the
 * scripted transport accepting the required real action, the transport
 * finishing its scripted emissions, or the production round-end overlay
 * appearing (phaser:round-end). Unknown / out-of-order events are ignored,
 * so duplicates can never skip or corrupt a step. Back is deliberately
 * absent here (gameplay steps advance the real scripted board and cannot be
 * safely unwound); Skip Chapter jumps to completion.
 *
 * Targets are declarative: `phaser` ids resolve through the bounds reporter,
 * `dom` ids through data-tutorial-target measurement. `passthrough` regions
 * stay dimmed but clickable (production invalid feedback keeps working).
 *
 * LOCALIZATION (Phase 8): steps are locale-neutral — they carry only stable
 * ids, targets, advancement rules, a stable section id, and structured
 * interpolation params sourced from fixtures. The visible title/body resolve
 * AT RENDER TIME through stepTitle()/stepBody() below against the `tutorial`
 * catalog (`steps.<id>.title` / `.body`, with `_desktop`/`_touch` context
 * variants where wording is device-specific), so a runtime language switch
 * relabels the open step without touching progression state. Translated text
 * is never used as identity anywhere in this module.
 */
import { tutorialTileId } from './fixtures/tutorialFixtures';
import { TUTORIAL_SCENARIOS, revealedPipSum, scenarioTileId } from './fixtures/tutorialScenarios';
import type { ScenarioId, ScriptedTransportEvent } from './ScriptedGameTransport';
import { IS_MOBILE_EXPERIENCE } from '../utils/deviceMode';
import { t } from '../i18n';

/** i18next context selecting device-specific wording (fixed at load, like
 *  the render mode). Keys without a `_touch`/`_desktop` variant fall back to
 *  their base key, so it is safe to pass on every resolution. */
export const TUTORIAL_DEVICE_CONTEXT: 'touch' | 'desktop' =
  IS_MOBILE_EXPERIENCE ? 'touch' : 'desktop';

// ── Step model ──────────────────────────────────────────────────────────

export type StepAdvance =
  | { on: 'continue' }
  | { on: 'action-accepted'; scenario: ScenarioId; action: number }
  | { on: 'action-done'; scenario: ScenarioId; action: number }
  | { on: 'round-end-shown' };

export interface StepTargets {
  phaser?: string[];
  dom?: string[];
  phaserPassthrough?: string[];
  /** Interactive steps let pointer events through the cutouts. */
  interactive?: boolean;
  /** Scenario transitions: full-screen dim with no cutout. */
  fullDim?: boolean;
}

/** Stable progress-section ids (Chapter 2 groups) → `tutorial:sections.*`. */
export type TutorialSectionId =
  | 'table' | 'legal' | 'doubles' | 'passing'
  | 'round' | 'scoring' | 'blocked' | 'matchwin';

export interface TutorialStep {
  id: string;
  /** The scenario that must be active while this step shows. */
  scenario: ScenarioId;
  /** Side effect the screen performs when ENTERING this step. */
  enter?: 'advance-scenario' | 'run-auto';
  advance: StepAdvance;
  targets: StepTargets;
  /** Stable progress-section id (never translated itself). */
  section: TutorialSectionId;
  /** Structured values interpolated into the step's catalog copy —
   *  real captured fixture data, never invented numbers. */
  params?: Record<string, string | number>;
  /** The dedicated drag-and-drop lesson: drag-only completion, directional
   *  guide, and overlay pointer-suppression while the drag is live. */
  dragLesson?: boolean;
}

/** Localized title/body/section for a step, resolved in the active language
 *  at call time (renderers re-invoke on every render, so a language switch
 *  relabels in place). */
export function stepTitle(step: TutorialStep): string {
  return String(t(`tutorial:steps.${step.id}.title` as never, {
    context: TUTORIAL_DEVICE_CONTEXT, ...step.params,
  } as never));
}
export function stepBody(step: TutorialStep): string {
  return String(t(`tutorial:steps.${step.id}.body` as never, {
    context: TUTORIAL_DEVICE_CONTEXT, ...step.params,
  } as never));
}
export function stepSectionLabel(step: TutorialStep): string {
  return String(t(`tutorial:sections.${step.section}` as never));
}

// Real captured data drives the worked examples — never invented numbers.
const S = TUTORIAL_SCENARIOS;
const DOMINO_PIPS = revealedPipSum(S.domino.finished!.revealed_players, 1);
const DOMINO_TILE = S.domino.boot.my_hand[0];
const MATCH_TILE = S.matchwin.boot.my_hand[0];
const MW_BEFORE = S.matchwin.boot.session_team_scores?.['0'] ?? 95;
const MW_AFTER = S.matchwin.finished!.team_scores['0'];
const BLOCKED_PIPS_A = revealedPipSum(S.blocked.finished!.revealed_players, 0);
const BLOCKED_PIPS_B = revealedPipSum(S.blocked.finished!.revealed_players, 1);
const tileName = (t: { value_a: number; value_b: number }) => `${t.value_a}–${t.value_b}`;

/** The end tiles the pass scenario leaves open (6|3 and 6|2 — see capture). */
const PASS_END_TILES = [`board-tile:${scenarioTileId('pass', 6, 3)}`, `board-tile:${scenarioTileId('pass', 6, 2)}`];

export const TUTORIAL_STEPS: TutorialStep[] = [
  // ── Section A — understand the table (scenario: legal) ────────────────
  {
    id: 'seat-you', scenario: 'legal', section: 'table',
    advance: { on: 'continue' },
    targets: { phaser: ['hand-rack'] },
  },
  {
    id: 'seat-partner', scenario: 'legal', section: 'table',
    advance: { on: 'continue' },
    targets: { phaser: ['seat:2'] },
  },
  {
    id: 'seat-opponents', scenario: 'legal', section: 'table',
    advance: { on: 'continue' },
    targets: { phaser: ['seat:1', 'seat:3'] },
  },
  {
    id: 'score', scenario: 'legal', section: 'table',
    advance: { on: 'continue' },
    targets: { dom: ['score-display'] },
  },
  {
    id: 'hand', scenario: 'legal', section: 'table',
    advance: { on: 'continue' },
    targets: { phaser: ['hand-rack'] },
  },

  // ── Section B — make legal plays (scenario: legal) ────────────────────
  {
    id: 'open66', scenario: 'legal', section: 'legal',
    advance: { on: 'action-accepted', scenario: 'legal', action: 0 },
    targets: { phaser: [`hand-tile:${tutorialTileId(6, 6)}`], interactive: true },
  },
  {
    id: 'watch-open', scenario: 'legal', section: 'legal',
    advance: { on: 'action-done', scenario: 'legal', action: 0 },
    targets: {},
  },
  {
    id: 'open-end', scenario: 'legal', section: 'legal',
    advance: { on: 'continue' },
    targets: { phaser: [`board-tile:${tutorialTileId(3, 3)}`] },
  },
  {
    id: 'match30', scenario: 'legal', section: 'legal', dragLesson: true,
    advance: { on: 'action-accepted', scenario: 'legal', action: 1 },
    targets: {
      phaser: [`hand-tile:${tutorialTileId(3, 0)}`, `board-tile:${tutorialTileId(3, 3)}`, 'placement-previews'],
      phaserPassthrough: ['hand-rack'],
      interactive: true,
    },
  },
  {
    id: 'two-ways', scenario: 'legal', section: 'legal',
    advance: { on: 'continue' },
    targets: {},
  },
  {
    id: 'watch-match', scenario: 'legal', section: 'legal',
    advance: { on: 'action-done', scenario: 'legal', action: 1 },
    targets: {},
  },
  {
    id: 'choose-ends', scenario: 'legal', section: 'legal',
    advance: { on: 'action-accepted', scenario: 'legal', action: 2 },
    targets: { phaser: [`hand-tile:${tutorialTileId(5, 0)}`, 'placement-previews'], phaserPassthrough: ['hand-rack'], interactive: true },
  },
  {
    id: 'watch-choose', scenario: 'legal', section: 'legal',
    advance: { on: 'action-done', scenario: 'legal', action: 2 },
    targets: {},
  },

  // ── Section C — doubles ───────────────────────────────────────────────
  {
    id: 'trans-double', scenario: 'legal', section: 'doubles',
    advance: { on: 'continue' },
    targets: { fullDim: true },
  },
  {
    id: 'double-hand', scenario: 'double', section: 'doubles', enter: 'advance-scenario',
    advance: { on: 'action-accepted', scenario: 'double', action: 0 },
    targets: { phaser: [`hand-tile:${scenarioTileId('double', 4, 4)}`], phaserPassthrough: ['hand-rack'], interactive: true },
  },
  {
    id: 'double-placed', scenario: 'double', section: 'doubles',
    advance: { on: 'continue' },
    targets: { phaser: [`board-tile:${scenarioTileId('double', 4, 4)}`] },
  },

  // ── Section D — passing ───────────────────────────────────────────────
  {
    id: 'trans-pass', scenario: 'double', section: 'passing',
    advance: { on: 'continue' },
    targets: { fullDim: true },
  },
  {
    id: 'pass-ends', scenario: 'pass', section: 'passing', enter: 'advance-scenario',
    advance: { on: 'continue' },
    targets: { phaser: PASS_END_TILES, phaserPassthrough: ['hand-rack'] },
  },
  {
    id: 'pass-knock', scenario: 'pass', section: 'passing',
    advance: { on: 'action-accepted', scenario: 'pass', action: 0 },
    targets: { phaser: ['knock'], interactive: true },
  },
  {
    id: 'pass-done', scenario: 'pass', section: 'passing',
    advance: { on: 'action-done', scenario: 'pass', action: 0 },
    targets: {},
  },

  // ── Section E — winning a round by domino ─────────────────────────────
  {
    id: 'trans-domino', scenario: 'pass', section: 'round',
    advance: { on: 'continue' },
    targets: { fullDim: true },
  },
  {
    id: 'domino-final', scenario: 'domino', section: 'round', enter: 'advance-scenario',
    advance: { on: 'action-accepted', scenario: 'domino', action: 0 },
    targets: { phaser: [`hand-tile:${DOMINO_TILE.id}`], interactive: true },
    params: { tile: tileName(DOMINO_TILE) },
  },
  {
    id: 'domino-wait', scenario: 'domino', section: 'round',
    advance: { on: 'round-end-shown' },
    targets: {},
  },
  {
    id: 'roundend-panel', scenario: 'domino', section: 'round',
    advance: { on: 'continue' },
    targets: { dom: ['roundend-panel'] },
  },

  // ── Section F — scoring ───────────────────────────────────────────────
  {
    id: 'scoring-losing', scenario: 'domino', section: 'scoring',
    advance: { on: 'continue' },
    targets: { dom: ['roundend-row-team-1'] },
    params: { pips: DOMINO_PIPS },
  },
  {
    id: 'scoring-score', scenario: 'domino', section: 'scoring',
    advance: { on: 'continue' },
    targets: { dom: ['roundend-scores'] },
    params: { pips: DOMINO_PIPS },
  },

  // ── Section G — blocked game ──────────────────────────────────────────
  {
    id: 'trans-blocked', scenario: 'domino', section: 'blocked',
    advance: { on: 'continue' },
    targets: { fullDim: true },
  },
  {
    id: 'blocked-intro', scenario: 'blocked', section: 'blocked', enter: 'advance-scenario',
    advance: { on: 'continue' },
    targets: { phaser: ['hand-rack'] },
  },
  {
    id: 'blocked-wait', scenario: 'blocked', section: 'blocked', enter: 'run-auto',
    advance: { on: 'round-end-shown' },
    targets: {},
  },
  {
    id: 'blocked-totals', scenario: 'blocked', section: 'blocked',
    advance: { on: 'continue' },
    targets: { dom: ['roundend-row-team-0', 'roundend-row-team-1'] },
    params: { a: BLOCKED_PIPS_A, b: BLOCKED_PIPS_B },
  },

  // ── Section H — winning the match ─────────────────────────────────────
  {
    id: 'trans-matchwin', scenario: 'blocked', section: 'matchwin',
    advance: { on: 'continue' },
    targets: { fullDim: true },
    params: { before: MW_BEFORE },
  },
  {
    id: 'matchwin-score', scenario: 'matchwin', section: 'matchwin', enter: 'advance-scenario',
    advance: { on: 'continue' },
    targets: { dom: ['score-display'] },
    params: { before: MW_BEFORE },
  },
  {
    id: 'matchwin-final', scenario: 'matchwin', section: 'matchwin',
    advance: { on: 'action-accepted', scenario: 'matchwin', action: 0 },
    targets: { phaser: [`hand-tile:${MATCH_TILE.id}`], interactive: true },
    params: { tile: tileName(MATCH_TILE) },
  },
  {
    id: 'matchwin-wait', scenario: 'matchwin', section: 'matchwin',
    advance: { on: 'round-end-shown' },
    targets: {},
  },
  {
    id: 'matchend-result', scenario: 'matchwin', section: 'matchwin',
    advance: { on: 'continue' },
    targets: { dom: ['matchend-outcome', 'matchend-teams'] },
    params: { after: MW_AFTER },
  },
  {
    id: 'matchend-choices', scenario: 'matchwin', section: 'matchwin',
    advance: { on: 'continue' },
    targets: { dom: ['matchend-actions'] },
  },
];

export const GAMEPLAY_TOTAL_STEPS = TUTORIAL_STEPS.length;

// ── Reducer ─────────────────────────────────────────────────────────────

export type GameplayEvent =
  | ScriptedTransportEvent
  | { type: 'continue' }
  | { type: 'round-end-shown' }
  | { type: 'skip-chapter' };

export interface GameplayDirectorState {
  /** Index into TUTORIAL_STEPS; === length ⇒ completion modal. */
  index: number;
  skipped: boolean;
  /** Latched action-done keys ("scenario:action"): scripted replies finish
   *  in the background, so a watch step entered AFTER its awaited
   *  action-done fired (the reader lingered on "Two ways to play") must
   *  auto-advance instead of stranding. */
  doneEvents: string[];
}

export const INITIAL_GAMEPLAY_STATE: GameplayDirectorState = { index: 0, skipped: false, doneEvents: [] };

function matches(advance: StepAdvance, evt: GameplayEvent): boolean {
  switch (advance.on) {
    case 'continue':
      return evt.type === 'continue';
    case 'action-accepted':
      return evt.type === 'action-accepted' && evt.scenario === advance.scenario && evt.action === advance.action;
    case 'action-done':
      return evt.type === 'action-done' && evt.scenario === advance.scenario && evt.action === advance.action;
    case 'round-end-shown':
      return evt.type === 'round-end-shown';
    default:
      return false;
  }
}

export function reduceGameplay(
  state: GameplayDirectorState,
  evt: GameplayEvent,
): GameplayDirectorState {
  if (evt.type === 'skip-chapter') {
    return state.index >= TUTORIAL_STEPS.length
      ? state
      : { ...state, index: TUTORIAL_STEPS.length, skipped: true };
  }

  const key = evt.type === 'action-done' ? `${evt.scenario}:${evt.action}` : null;
  const doneEvents = key && !state.doneEvents.includes(key)
    ? [...state.doneEvents, key]
    : state.doneEvents;

  const step = TUTORIAL_STEPS[state.index];
  if (!step) return doneEvents === state.doneEvents ? state : { ...state, doneEvents };

  let index = state.index;
  if (matches(step.advance, evt)) index++;

  // Latch: skip over any waiting-for-action-done step whose event already
  // fired while an earlier step held the reader's attention.
  while (index < TUTORIAL_STEPS.length) {
    const next = TUTORIAL_STEPS[index].advance;
    if (next.on === 'action-done' && doneEvents.includes(`${next.scenario}:${next.action}`)) {
      index++;
    } else {
      break;
    }
  }

  if (index === state.index && doneEvents === state.doneEvents) return state;
  return { ...state, index, doneEvents };
}
